import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { KycApi } from '../apis/kyc-basic.api';
import { AccountInforData } from '../interfaces/account.interface';
import { CsPopupService } from '../components/cs-popup/cs-popup.service';

/** Shows the generic, once-per-login reminder for unresolved custom documents. */
@Injectable({ providedIn: 'root' })
export class KycDocumentPendingPopupService {
  public constructor(
    private readonly appService: AppService,
    private readonly kycApi: KycApi,
    private readonly csPopupService: CsPopupService,
    private readonly router: Router,
  ) {}

  public async showForUnresolvedCustomDocuments(account: AccountInforData): Promise<void> {
    if (!account.isDocsPending || !account.uid || this.hasShownFor(account.uid)) {
      return;
    }

    try {
      const documents = await firstValueFrom(this.kycApi.getCustomDocuments());
      const hasUnresolvedCustomDocument = documents.some(
        document => document.status !== 'Unrequested' && document.status !== 'Verified',
      );
      if (!hasUnresolvedCustomDocument) {
        return;
      }

      sessionStorage.setItem(this.storageKey(account.uid), 'true');
      this.csPopupService.info({
        header: 'Verification Required',
        message: 'You have verification documents waiting for your attention.',
        confirmText: 'Go to Verification',
        confirmCallback: () => {
          void this.router.navigate([this.appService.languageCode, 'settings', 'verification']);
        },
      });
    } catch (error) {
      // A reminder must never prevent a successful login when the document list is temporarily unavailable.
      console.error('Failed to check unresolved custom documents after login', error);
    }
  }

  private hasShownFor(uid: string): boolean {
    return sessionStorage.getItem(this.storageKey(uid)) === 'true';
  }

  private storageKey(uid: string): string {
    return `kyc-custom-document-reminder:${uid}`;
  }
}
